import { useCarousel, CarouselNavButtons } from "@/components/ui/Carousel";
import { StarsIcon, VerifiedIcon } from "@/components/common/Icons";
import { Button } from "@/components/ui/Button";

/**
 * VideoCard Component
 */
function VideoCard({ video }) {
  return (
    <div
      className="relative overflow-hidden aspect-[9/14] scroll-snap-stop-always
                    max-md:rounded-[1.2rem] max-md:aspect-[9/12]"
    >
      {video.isImage ? (
        <img
          src={video.src}
          alt={video.headline}
          className="w-full h-full object-cover rounded-[1.5rem] block max-md:rounded-[1.2rem]"
          loading="lazy"
        />
      ) : (
        <video
          src={video.src}
          poster={video.poster}
          className="w-full h-full object-cover rounded-[1.5rem] bg-cover bg-center bg-no-repeat
                     max-md:rounded-[1.2rem]"
          muted
          playsInline
          loop
          loading="lazy"
        />
      )}

      {/* Overlay */}
      <div
        className="absolute bottom-0 left-0 right-0 py-20 px-4 pb-4
                      video-overlay-gradient text-white font-[480]
                      flex flex-col gap-1 rounded-[1.5rem]
                      max-md:p-4 max-md:gap-2 max-md:rounded-[1.2rem]"
      >
        <div className="mb-1">
          <StarsIcon className="w-20 h-5" color="var(--color-secondary)" />
        </div>
        <p className="text-[1.05rem] leading-relaxed max-md:text-[1.1rem] max-md:leading-[1.3]">
          {video.headline}
        </p>
        <div className="mt-0.5 flex items-center gap-1.5 text-[0.85rem] text-white opacity-80 max-md:mt-0.5 max-md:gap-1.5">
          <span className="font-medium text-white text-[0.85rem] max-md:font-[480] max-md:text-[0.95rem]">
            {video.name}
          </span>
          <div className="flex items-center gap-1 opacity-90 max-md:text-[0.95rem] max-md:font-[480]">
            <VerifiedIcon className="w-3.5 h-3.5 max-md:w-3 max-md:h-3" />
            <span>Verified Buyer</span>
          </div>
        </div>
      </div>
    </div>
  );
}

/**
 * VideoTestimonialsSection Component
 */
export function VideoTestimonialsSection({ videos }) {
  const isMobile = typeof window !== "undefined" && window.innerWidth <= 767;

  const acfData = window.post_template || [];
  const ctaLink = window.cta_link || "#";

  const { emblaRef, scrollPrev, scrollNext } = useCarousel({
    align: "center",
    dragFree: !isMobile,
    loop: true,
    containScroll: true,
  });

  return (
    <section
      id="real-stories"
      className="py-12 pb-6 bg-accent-bg max-md:py-8 max-md:pb-0"
    >
      {/* Header */}
      <div className="text-center mb-12 max-md:mb-8">
        <h2
          className="font-family-title text-[2.5rem] font-normal mb-4 text-main
                       max-md:text-[1.8rem] max-md:leading-[1.25] max-md:mb-3"
        >
          Real Results, Real People
        </h2>
        <p
          className="text-[1.1rem] text-gray max-w-[600px] mx-auto leading-relaxed
                      max-md:text-[0.95rem] max-md:max-w-none"
        >
          See how our customers transformed their wellness journey with daily
          probiotic support
        </p>
      </div>

      {/* Carousel */}
      <div className="embla p-0">
        <div
          ref={emblaRef}
          className="embla__viewport overflow-hidden cursor-grab select-none"
        >
          <div className="embla__container flex gap-6 max-md:gap-4">
            {videos.map((video, index) => (
              <div
                key={video.id}
                className={`embla__slide flex-[0_0_280px]
                           max-md:flex-[0_0_85%]
                           ${index === 0 ? "ml-6 max-md:ml-4" : ""}`}
              >
                <VideoCard video={video} />
              </div>
            ))}
          </div>
        </div>

        {/* Navigation */}
        <CarouselNavButtons
          onPrev={scrollPrev}
          onNext={scrollNext}
          className="max-md:mt-5"
        />
      </div>

      {/* Mobile CTA */}
      <div className="hidden max-md:block px-4">
        <Button
          href={ctaLink}
          variant="secondary"
          mobileOnly
          className="w-full mt-4"
        >
          {acfData.tr_cta_text}
        </Button>
      </div>
    </section>
  );
}

export default VideoTestimonialsSection;
